import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, XCircle, RotateCcw, MapPin, Phone, Mail, Package, Clock, User, Printer, ExternalLink } from "lucide-react";
import { businessApi, API } from "../../lib/api";
import { STATUS_LABEL } from "./BusinessShipmentsListPage";

const CANCELLABLE = new Set(["created", "ready_for_pickup", "picked_up"]);

export default function BusinessShipmentDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await businessApi().get(`/business/shipments/${id}`);
      setDoc(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente");
      nav("/business/shipments");
    }
    setLoading(false);
  }, [id, nav]);

  React.useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (!window.confirm("Annullér denne forsendelse?")) return;
    setBusy("cancel");
    try {
      await businessApi().post(`/business/shipments/${id}/cancel`);
      toast.success("Forsendelse annulleret");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke annullere"); }
    finally { setBusy(""); }
  };

  const requestRedelivery = async () => {
    if (!window.confirm("Anmod om genlevering? Admin vil gennemgå og godkende.")) return;
    setBusy("redelivery");
    try {
      const { data } = await businessApi().post(`/business/shipments/${id}/redelivery-request`);
      toast.success(data.message || "Anmodning sendt");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="text-brand-text2 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Indlæser...</div>;
  if (!doc) return null;

  const lbl = STATUS_LABEL[doc.status] || { label: doc.status, color: "bg-gray-100 text-gray-700" };
  const canCancel = CANCELLABLE.has(doc.status);
  const canRedelivery = doc.status === "failed_delivery";

  return (
    <div className="space-y-5 max-w-5xl" data-testid="business-shipment-detail-page">
      <Link to="/business/shipments" className="inline-flex items-center gap-1 text-sm text-brand-text2 hover:text-brand-dark" data-testid="biz-detail-back">
        <ArrowLeft size={14}/> Tilbage til forsendelser
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-brand-red font-semibold">Forsendelse</div>
          <h1 className="font-display font-bold text-3xl text-brand-dark mt-1 font-mono" data-testid="biz-detail-number">{doc.shipment_number}</h1>
          {doc.customer_reference && <div className="text-xs text-brand-text2 mt-1">Din ref: <span className="font-mono">{doc.customer_reference}</span></div>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className={`text-sm px-3 py-1 ${lbl.color}`} data-testid="biz-detail-status">{lbl.label}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {canCancel && (
          <button onClick={cancel} disabled={busy === "cancel"} className="bg-red-50 border border-red-300 text-red-700 hover:bg-red-100 px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="biz-detail-cancel">
            {busy === "cancel" ? <Loader2 className="animate-spin" size={14}/> : <XCircle size={14}/>} Annullér forsendelse
          </button>
        )}
        {canRedelivery && (
          <button onClick={requestRedelivery} disabled={busy === "redelivery"} className="bg-orange-50 border border-orange-300 text-orange-800 hover:bg-orange-100 px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="biz-detail-redelivery">
            {busy === "redelivery" ? <Loader2 className="animate-spin" size={14}/> : <RotateCcw size={14}/>} Anmod genlevering
          </button>
        )}
        <LabelDownloadButtons shipmentId={id}/>
        <a href={`/track/${id}`} target="_blank" rel="noopener noreferrer" className="bg-white border border-brand px-4 py-2 text-sm inline-flex items-center gap-1 hover:bg-brand-bg2" data-testid="biz-detail-public-track">
          <ExternalLink size={14}/> Åbn public tracking
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Tracking timeline */}
        <section className="lg:col-span-2 bg-white border border-brand p-5" data-testid="biz-detail-timeline">
          <h2 className="font-display font-bold text-xl text-brand-dark mb-4">Tracking</h2>
          <ol className="relative border-l-2 border-brand-bg2 pl-5 space-y-4">
            {(doc.tracking_events || []).map((ev) => {
              const evLbl = STATUS_LABEL[ev.status] || { label: ev.status, color: "bg-gray-100" };
              return (
                <li key={ev.id} className="relative">
                  <span className={`absolute -left-7 top-1 w-3 h-3 rounded-full bg-brand-red border-2 border-white`}></span>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`text-xs px-1.5 py-0.5 ${evLbl.color}`}>{evLbl.label}</span>
                    <span className="text-xs text-brand-text2"><Clock size={10} className="inline mr-0.5"/>{new Date(ev.ts_iso).toLocaleString("da-DK")}</span>
                    <span className="text-xs text-brand-text2 ml-auto">{ev.source}</span>
                  </div>
                  {ev.note && <div className="text-sm text-brand-dark mt-0.5">{ev.note}</div>}
                </li>
              );
            })}
            {(doc.tracking_events || []).length === 0 && <li className="text-sm text-brand-text2">Ingen events endnu</li>}
          </ol>
        </section>

        {/* Side panel */}
        <aside className="space-y-4">
          <div className="bg-white border border-brand p-4" data-testid="biz-detail-recipient">
            <div className="text-xs uppercase tracking-wider text-brand-text2 mb-2">Modtager</div>
            <div className="font-semibold text-brand-dark"><User size={14} className="inline mr-1"/>{doc.recipient_name}</div>
            <div className="text-sm text-brand-text2 mt-1"><MapPin size={12} className="inline mr-1"/>{doc.delivery_address}, {doc.delivery_postal_code} {doc.delivery_city}</div>
            {doc.recipient_phone && <div className="text-sm text-brand-text2"><Phone size={12} className="inline mr-1"/>{doc.recipient_phone}</div>}
            {doc.recipient_email && <div className="text-sm text-brand-text2"><Mail size={12} className="inline mr-1"/>{doc.recipient_email}</div>}
            {doc.delivery_notes && <div className="text-xs italic text-brand-text2 mt-2 pt-2 border-t border-gray-100">&quot;{doc.delivery_notes}&quot;</div>}
          </div>

          <div className="bg-white border border-brand p-4">
            <div className="text-xs uppercase tracking-wider text-brand-text2 mb-2">Afhentning</div>
            <div className="font-semibold text-brand-dark"><MapPin size={14} className="inline mr-1"/>{doc.pickup_location_name}</div>
            <div className="text-sm text-brand-text2 mt-1">{doc.pickup_address}, {doc.pickup_postal_code} {doc.pickup_city}</div>
            <div className="text-xs text-brand-text2 mt-1">Zone: {doc.pickup_zone_name}</div>
          </div>

          <div className="bg-white border border-brand p-4">
            <div className="text-xs uppercase tracking-wider text-brand-text2 mb-2">Pakke</div>
            <div className="text-sm"><Package size={12} className="inline mr-1"/>{doc.length_cm}×{doc.width_cm}×{doc.height_cm} cm · {doc.weight_kg} kg · {doc.volume_l} L</div>
            <div className="text-sm text-brand-text2">{doc.colli_count} colli · {doc.product === "same_day_b2b" ? "Same Day" : "Next Day"}</div>
            <div className="text-sm font-semibold text-brand-dark mt-2 pt-2 border-t border-gray-100">{doc.price?.total_dkk?.toFixed(2)} kr <span className="text-xs text-brand-text2">ekskl. moms</span></div>
            <div className="text-xs text-brand-text2">Tier: {doc.price?.tier} · {doc.price?.unit_price_dkk} kr/pakke</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function LabelDownloadButtons({ shipmentId }) {
  const [busy, setBusy] = React.useState(false);
  const download = async (fmt) => {
    setBusy(true);
    try {
      const tk = localStorage.getItem("ema_business_token");
      const res = await fetch(`${API}/business/shipments/${shipmentId}/label.pdf?fmt=${fmt}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) throw new Error("Label fejl");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`Label genereret (${fmt})`);
    } catch { toast.error("Kunne ikke generere label"); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex border border-brand" data-testid="biz-detail-label-group">
      <button onClick={() => download("thermal")} disabled={busy} className="bg-white px-3 py-2 text-sm inline-flex items-center gap-1 hover:bg-brand-bg2 border-r border-brand" data-testid="biz-detail-label-thermal">
        {busy ? <Loader2 className="animate-spin" size={14}/> : <Printer size={14}/>} Label 10×15
      </button>
      <button onClick={() => download("a6")} disabled={busy} className="bg-white px-3 py-2 text-sm inline-flex items-center gap-1 hover:bg-brand-bg2" data-testid="biz-detail-label-a6">
        Label A6
      </button>
    </div>
  );
}

